import type { SupabaseClient } from '../../lib/supabase';
import { inertText } from './inertHtml';

/**
 * The Messages page's rooms: the shared channels (Drop 1, 20270168000100:
 * Agents, Hub help, Team) plus, from Drop 3b-1 (20270180000100), the
 * signed-in person's direct messages and group chats.
 *
 * Read as an ordinary project select: a room is a project row with
 * is_room = true, and RLS (projects_select_authorized, narrowed for
 * members_only rooms by 20270176000100) decides which rows come back. A DM or
 * a group is members_only, so only the people in it ever receive its row;
 * nothing here filters for privacy, the database does.
 *
 * Deliberately NOT fetchProjects: that excludes is_room rows on purpose
 * (Drop 3a item 5), so the room list has to ask separately.
 */

export type RoomKind = 'channel' | 'dm' | 'group';

export interface RoomListItem {
  id: string;
  /** The stored name. For a DM this is always "Direct message"; show roomTitle instead. */
  name: string;
  description: string | null;
  kind: RoomKind;
  /**
   * Only its members can open it: every DM and group, and a channel whose
   * visibility is members_only (today the Agents room, 20270174000100). The
   * page treats all of these alike for @ mentions (F-A, next drop after
   * 3b-2): the picker offers the members only.
   */
  membersOnly: boolean;
}

/** One person in a DM or group, for its title and member line. */
export interface RoomMember {
  userId: string;
  name: string;
  avatarUrl: string | null;
}

function toKind(value: string | null): RoomKind {
  // room_kind is set on every room by 20270180000100 (constraint
  // projects_room_kind_matches_is_room). A row read before that migration
  // runs has none; treating it as a channel keeps 3a behaviour.
  return value === 'dm' || value === 'group' ? value : 'channel';
}

export async function fetchRooms(client: SupabaseClient): Promise<RoomListItem[]> {
  const { data, error } = await client
    .from('projects')
    .select('id, name, description, room_kind, visibility')
    .eq('is_room', true)
    .order('name');
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => {
    const kind = toKind(row.room_kind);
    return {
      id: row.id,
      name: row.name,
      description: row.description,
      kind,
      // A DM or group is always members_only (a check constraint,
      // 20270180000100); saying so here too keeps a stray row safe.
      membersOnly: kind !== 'channel' || row.visibility === 'members_only',
    };
  });
}

function personName(profile: { display_name: string; email: string } | null): string {
  if (profile === null) {
    return '';
  }
  return profile.display_name.trim() !== '' ? profile.display_name : profile.email;
}

/**
 * The members of the given projects, keyed by project id. Asked for the
 * caller's members-only rooms (DMs, groups, private channels) and for the
 * projects behind the Alex Huddle threads. An all_tap channel is not asked
 * for: its @ picker offers everyone. RLS (project_members_select_authorized,
 * can_view_project) returns rows only for projects the caller can view.
 */
export async function fetchRoomMembers(
  client: SupabaseClient,
  roomIds: string[],
): Promise<Map<string, RoomMember[]>> {
  const out = new Map<string, RoomMember[]>();
  if (roomIds.length === 0) {
    return out;
  }
  const { data, error } = await client
    .from('project_members')
    .select('project_id, user_id, profiles:user_id (display_name, email, avatar_url)')
    .in('project_id', roomIds);
  if (error) {
    throw new Error(error.message);
  }
  for (const row of (data ?? []) as unknown as {
    project_id: string;
    user_id: string;
    profiles: { display_name: string; email: string; avatar_url: string | null } | null;
  }[]) {
    const list = out.get(row.project_id) ?? [];
    list.push({
      userId: row.user_id,
      name: personName(row.profiles),
      avatarUrl: row.profiles?.avatar_url ?? null,
    });
    out.set(row.project_id, list);
  }
  for (const list of out.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return out;
}

/**
 * What a room is called on this page. A DM shows the other person's name, never
 * the stored "Direct message" (both members can rename a DM through the
 * ordinary project update grant, findings-dev4-drop3b1-db.md, so the stored
 * name is not trustworthy either). A group and a channel show their own name.
 */
export function roomTitle(
  room: RoomListItem,
  members: RoomMember[] | undefined,
  myId: string,
): string {
  if (room.kind !== 'dm') {
    return room.name;
  }
  const other = (members ?? []).find((member) => member.userId !== myId);
  if (other === undefined || other.name === '') {
    return 'Direct message';
  }
  return other.name;
}

/** "You, Nez, Senku": the group's member line (mockup Main.dc.html:61). */
export function groupMemberLine(members: RoomMember[] | undefined, myId: string): string {
  const names = (members ?? [])
    .filter((member) => member.userId !== myId)
    .map((member) => (member.name === '' ? 'Someone' : member.name));
  return ['You', ...names].join(', ');
}

/** The latest comment in each room, for the sidebar preview line and time. */
export interface RoomLatestMessage {
  projectId: string;
  authorName: string;
  bodyPreview: string;
  createdAt: string;
}

function plainTextPreview(body: string): string {
  // Inert parse: a live div would load an <img onerror> from the body.
  const text = inertText(body);
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

export async function fetchRoomLatestMessages(
  client: SupabaseClient,
  roomIds: string[],
): Promise<Map<string, RoomLatestMessage>> {
  if (roomIds.length === 0) {
    return new Map();
  }
  const { data, error } = await client
    .from('task_comments')
    .select('project_id, body, created_at, profiles:author_id (display_name, email)')
    .in('project_id', roomIds)
    .is('task_id', null)
    // Drop 3c (D3, data-model 3): a thread reply is not the room's latest
    // message; the parent's "N replies" line is where it shows.
    .is('parent_comment_id', null)
    .order('created_at', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  const out = new Map<string, RoomLatestMessage>();
  for (const row of (data ?? []) as unknown as {
    project_id: string;
    body: string;
    created_at: string;
    profiles: { display_name: string; email: string } | null;
  }[]) {
    if (out.has(row.project_id)) {
      continue;
    }
    out.set(row.project_id, {
      projectId: row.project_id,
      authorName: personName(row.profiles),
      bodyPreview: plainTextPreview(row.body),
      createdAt: row.created_at,
    });
  }
  return out;
}

/* ─── Unread counts (20270180000100: room_reads, my_room_unread, mark_room_read) ─── */

/**
 * my_room_unread caps every room at this number (20270180000100, the
 * least(..., 99) in my_room_unread), so a 99 from the database means
 * "99 or more", never exactly 99.
 */
export const ROOM_UNREAD_CAP = 99;

/** Unread messages per room id, only for rooms the caller can view. */
export async function fetchRoomUnread(client: SupabaseClient): Promise<Map<string, number>> {
  const { data, error } = await client.rpc('my_room_unread');
  if (error) {
    throw new Error(error.message);
  }
  const out = new Map<string, number>();
  // Array check, not `?? []`: a stubbed or odd reply must not crash the sidebar.
  for (const row of Array.isArray(data) ? data : []) {
    out.set(row.project_id, row.unread);
  }
  return out;
}

export async function markRoomRead(client: SupabaseClient, roomId: string): Promise<void> {
  const { error } = await client.rpc('mark_room_read', { p_project_id: roomId });
  if (error) {
    throw new Error(error.message);
  }
}

/**
 * The text of an unread badge, or null when there is nothing to show.
 * Hidden at 0; "99+" above 99. A single room at the database's cap reads
 * "99+" as well, because the cap means the real number may be higher.
 */
export function unreadBadgeText(count: number, atCap = false): string | null {
  if (!Number.isFinite(count) || count <= 0) {
    return null;
  }
  if (count > ROOM_UNREAD_CAP || atCap) {
    return '99+';
  }
  return String(Math.floor(count));
}

/** One room's badge text, from the capped number my_room_unread returns. */
export function roomUnreadBadgeText(count: number | undefined): string | null {
  const value = count ?? 0;
  return unreadBadgeText(value, value >= ROOM_UNREAD_CAP);
}

/** The Messages total in the app sidebar: the sum across every room. */
export function totalUnreadBadgeText(counts: Map<string, number> | undefined): string | null {
  let total = 0;
  let atCap = false;
  for (const value of counts?.values() ?? []) {
    if (value > 0) {
      total += value;
    }
    if (value >= ROOM_UNREAD_CAP) {
      atCap = true;
    }
  }
  return unreadBadgeText(total, atCap);
}

/* ─── Creating DMs and groups (create_dm / create_group_room) ─── */

export async function createDm(client: SupabaseClient, otherUserId: string): Promise<string> {
  const { data, error } = await client.rpc('create_dm', { p_other_user: otherUserId });
  if (error) {
    throw new Error(error.message);
  }
  return data.id;
}

export async function createGroupRoom(
  client: SupabaseClient,
  input: { name: string; memberIds: string[] },
): Promise<string> {
  const { data, error } = await client.rpc('create_group_room', {
    p_name: input.name,
    p_member_ids: input.memberIds,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data.id;
}

/** The most people a group holds, the caller included (create_group_room). */
export const GROUP_MAX_PEOPLE = 25;
export const GROUP_NAME_MAX = 80;

/** Plain words for every refusal create_dm / create_group_room can raise. */
export function roomErrorMessage(raw: string): string {
  if (raw.includes('not_authorized_to_create_room')) {
    return 'Only TAP members can start a chat.';
  }
  if (raw.includes('dm_needs_another_person')) {
    return 'Pick someone other than yourself.';
  }
  if (raw.includes('group_needs_another_person')) {
    return 'Add at least one other person.';
  }
  if (raw.includes('group_is_full')) {
    return `A group holds at most ${GROUP_MAX_PEOPLE} people, you included.`;
  }
  if (raw.includes('room_name_required')) {
    return 'Give the group a name.';
  }
  if (raw.includes('room_name_too_long')) {
    return `Keep the name to ${GROUP_NAME_MAX} characters.`;
  }
  if (raw.includes('room_member_must_be_active_tap_member')) {
    return 'Chats are only for active TAP admins and members.';
  }
  if (raw.includes('not_authenticated')) {
    return 'Sign in again, then try once more.';
  }
  return 'That chat could not be started. Try again.';
}

/** A person who can be in a DM or group: an active TAP admin or member. */
export interface ChatPerson {
  id: string;
  name: string;
}

/**
 * Everyone a DM or group may hold (create_dm / create_group_room refuse the
 * rest): active TAP admins and members, agents included (their accounts are
 * tap_member). No viewers, no suppliers.
 */
export async function fetchChatPeople(client: SupabaseClient): Promise<ChatPerson[]> {
  const { data, error } = await client
    .from('profiles')
    .select('id, display_name, email')
    .eq('account_type', 'tap')
    .eq('is_active', true)
    .in('internal_role', ['tap_admin', 'tap_member'])
    .order('display_name', { ascending: true });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({ id: row.id, name: personName(row) }));
}
